import type { HeroCarouselSlide } from "@/components/ui/custom/hero-carousel/HeroCarousel";
import { useCarouselAnalytics } from "@/context/AnalyticsProvider";
import { useRouter } from "next/navigation";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getBackgroundDuration } from "./CarouselBackgrounds";
import { DEFAULT_CAROUSEL_SLIDES } from "./defaultCarouselSlides";

type CarouselState = {
  slides: HeroCarouselSlide[];
  currentIndex: number;
  currentSlide: HeroCarouselSlide;
  videoReplay: number;
  handleSlideChange: (index: number) => void;
  handleTimerReset: () => void;
  router: ReturnType<typeof useRouter>;
  track: ReturnType<typeof useCarouselAnalytics>["track"];
  swipeHandlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
  };
};

const HeroCarouselContext = createContext<CarouselState | null>(null);

const useHeroCarouselLogic = (slides: HeroCarouselSlide[]): CarouselState => {
  const router = useRouter();
  const { track } = useCarouselAnalytics();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoReplay, setVideoReplay] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const resetTimer = useCallback(
    (slideIndex?: number) => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      const targetIndex = slideIndex !== undefined ? slideIndex : currentIndex;
      const currentSlide = slides[targetIndex];
      const delay = getBackgroundDuration(currentSlide);

      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
      }, delay);
    },
    [slides, currentIndex],
  );
  useEffect(() => {
    resetTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [resetTimer]);

  const handleSlideChange = useCallback(
    (index: number) => {
      const newSlide = slides[index];

      if (newSlide?.videoUrl) {
        setVideoReplay((prev) => prev + 1);
      }

      setCurrentIndex(index);
    },
    [slides],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const threshold = 50;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          const prevIndex = (currentIndex - 1 + slides.length) % slides.length;
          handleSlideChange(prevIndex);
          resetTimer(prevIndex);
        } else {
          const nextIndex = (currentIndex + 1) % slides.length;
          handleSlideChange(nextIndex);
          resetTimer(nextIndex);
        }
      }

      touchStartRef.current = null;
    },
    [currentIndex, slides.length, handleSlideChange, resetTimer],
  );

  return {
    slides,
    currentIndex,
    currentSlide: slides[currentIndex],
    videoReplay,
    handleSlideChange,
    handleTimerReset: resetTimer,
    router,
    track,
    swipeHandlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
    },
  };
};

export const useHeroCarousel = (): CarouselState => {
  const context = useContext(HeroCarouselContext);
  if (!context) {
    throw new Error(
      "useHeroCarousel must be used within a HeroCarouselProvider",
    );
  }
  return context;
};

export interface HeroCarouselProviderProps {
  children?: ReactNode;
  slides?: HeroCarouselSlide[];
}

export function HeroCarouselProvider({
  children,
  slides,
}: HeroCarouselProviderProps) {
  return (
    <HeroCarouselContext.Provider
      value={useHeroCarouselLogic(slides ?? DEFAULT_CAROUSEL_SLIDES)}
    >
      {children}
    </HeroCarouselContext.Provider>
  );
}
